import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { 
  FiInfo, 
  FiCalendar, 
  FiImage, 
  FiGift, 
  FiPlus, 
  FiX, 
  FiLoader, 
  FiCheck,
  FiClock,
  FiUsers,
  FiDollarSign,
  FiFileText,
  FiTag,
  FiFolder
} from "react-icons/fi";
import { RiTicketLine } from "react-icons/ri";

const CreateCampaign = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  // State for custom prize categories
  const [customPrizeCategories, setCustomPrizeCategories] = useState([]);
  const [showCustomInput, setShowCustomInput] = useState({}); // { index: boolean }
  const [customCategoryValue, setCustomCategoryValue] = useState({}); // { index: string }
  
  const [prizes, setPrizes] = useState([{ 
    prizeName: "", 
    prizeCategory: "general",
    description: "",
    position: 1,
    prizeAmount: "" // for cash prizes
  }]);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    campCateId: "",
    startDate: "",
    endDate: "",
    registrationEndDate: "",
    price: "",
    noOfTickets: "",
    noOfWinners: "",
  });

  // Campaign images state - for ALL campaign types
  const [campaignImages, setCampaignImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  
  // Prize images state - for ALL campaign types
  const [prizeImages, setPrizeImages] = useState({}); // { index: File }
  const [prizeImagePreviews, setPrizeImagePreviews] = useState({}); // { index: previewUrl }

  const apiUrl = process.env.REACT_APP_API_URL || "https://api.dreamtoday.in";

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      // Clean up all previews
      imagePreviews.forEach(preview => URL.revokeObjectURL(preview));
      Object.values(prizeImagePreviews).forEach(preview => URL.revokeObjectURL(preview));
    };
  }, [imagePreviews, prizeImagePreviews]);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await axios.get(`${apiUrl}/api/admin/get-categories`, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  // Get selected category details
  const getSelectedCategory = () => {
    return categories.find(cat => cat._id === formData.campCateId);
  };

  const isDailyCampaign = () => {
    const selectedCat = getSelectedCategory();
    return selectedCat?.name?.toLowerCase() === "daily";
  };

  const isWeeklyMonthly = () => {
    const selectedCat = getSelectedCategory();
    const name = selectedCat?.name?.toLowerCase();
    return name === "weekly" || name === "monthly";
  };

  // Check if any category is selected (for showing conditional fields)
  const isCategorySelected = () => {
    return formData.campCateId !== "";
  };

  // Get all prize category options including custom ones
  const getPrizeCategoryOptions = (isDaily) => {
    const defaultOptions = isDaily 
      ? [
          { value: "cash", label: "Cash" },
          { value: "bonus", label: "Bonus" },
          { value: "jackpot", label: "Jackpot" }
        ]
      : [
          { value: "general", label: "General" },
          { value: "electronics", label: "Electronics" },
          { value: "fashion", label: "Fashion" },
          { value: "home", label: "Home & Living" },
          { value: "sports", label: "Sports" },
          { value: "other", label: "Other" }
        ];
    
    // Add custom categories
    const customOptions = customPrizeCategories.map(cat => ({
      value: cat,
      label: cat
    }));
    
    return [...defaultOptions, ...customOptions];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset prizes when category changes
    if (name === "campCateId") {
      const selectedCat = categories.find(cat => cat._id === value);
      if (selectedCat) {
        const isDaily = selectedCat.name?.toLowerCase() === "daily";
        setPrizes([{ 
          prizeName: "", 
          prizeCategory: isDaily ? "cash" : "general",
          description: "",
          position: 1,
          prizeAmount: ""
        }]);
        // Clear prize images
        setPrizeImages({});
        setPrizeImagePreviews({});
        // Clear custom category states
        setCustomPrizeCategories([]);
        setShowCustomInput({});
        setCustomCategoryValue({});
        // Clear campaign images
        setCampaignImages([]);
        setImagePreviews([]);
      }
    }
  };

  // Handle campaign images (max 5) - for ALL campaign types
  const handleCampaignImages = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 5) {
      toast.error("You can only upload up to 5 images");
      return;
    }
    
    // Validate each file
    for (let file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`Image ${file.name} is too large. Max 5MB`);
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        return;
      }
    }
    
    setCampaignImages(files);
    
    // Clean up old previews
    imagePreviews.forEach(preview => URL.revokeObjectURL(preview));
    
    // Create new previews
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  // Handle prize image (one per prize) - for ALL campaign types
  const handlePrizeImage = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Prize image size should be less than 5MB");
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }
    
    // Store the file in prizeImages state with the index as key
    setPrizeImages(prev => ({
      ...prev,
      [index]: file
    }));

    // Clean up old preview for this index
    if (prizeImagePreviews[index]) {
      URL.revokeObjectURL(prizeImagePreviews[index]);
    }

    // Create new preview
    const preview = URL.createObjectURL(file);
    setPrizeImagePreviews(prev => ({
      ...prev,
      [index]: preview
    }));
  };

  const handlePrizeChange = (index, field, value) => {
    const updatedPrizes = [...prizes];
    updatedPrizes[index][field] = value;
    
    // Auto-set position based on index
    if (field === 'prizeName' || field === 'prizeCategory') {
      updatedPrizes[index].position = index + 1;
    }
    
    setPrizes(updatedPrizes);
  };

  const addPrize = () => {
    if (prizes.length >= 10) {
      toast.error("Maximum 10 prizes allowed");
      return;
    }
    
    const isDaily = isDailyCampaign();
    const newPrize = {
      prizeName: "",
      prizeCategory: isDaily ? "cash" : "general",
      description: "",
      position: prizes.length + 1,
      prizeAmount: "" // for cash prizes
    };
    
    setPrizes([...prizes, newPrize]);
  };

  const removePrize = (index) => {
    const updatedPrizes = prizes.filter((_, i) => i !== index);
    
    // Update positions
    updatedPrizes.forEach((prize, idx) => {
      prize.position = idx + 1;
    });
    
    setPrizes(updatedPrizes);
    
    // Clean up prize image for this index
    if (prizeImagePreviews[index]) {
      URL.revokeObjectURL(prizeImagePreviews[index]);
    }
    
    // Remove prize image from state
    const updatedPreviews = { ...prizeImagePreviews };
    const updatedImages = { ...prizeImages };
    delete updatedPreviews[index];
    delete updatedImages[index];
    
    // Re-index remaining prize images
    const reindexedImages = {};
    const reindexedPreviews = {};
    
    Object.keys(updatedImages).forEach((key, idx) => {
      if (parseInt(key) > index) {
        // Shift down
        reindexedImages[idx] = updatedImages[key];
        reindexedPreviews[idx] = updatedPreviews[key];
      } else if (parseInt(key) < index) {
        // Keep same
        reindexedImages[parseInt(key)] = updatedImages[key];
        reindexedPreviews[parseInt(key)] = updatedPreviews[key];
      }
    });
    
    setPrizeImages(reindexedImages);
    setPrizeImagePreviews(reindexedPreviews);

    // Clean up custom category states for this index
    const updatedShowCustom = { ...showCustomInput };
    const updatedCustomValue = { ...customCategoryValue };
    delete updatedShowCustom[index];
    delete updatedCustomValue[index];
    
    // Re-index
    const reindexedShowCustom = {};
    const reindexedCustomValue = {};
    
    Object.keys(updatedShowCustom).forEach((key, idx) => {
      if (parseInt(key) > index) {
        reindexedShowCustom[idx] = updatedShowCustom[key];
        reindexedCustomValue[idx] = updatedCustomValue[key];
      } else if (parseInt(key) < index) {
        reindexedShowCustom[parseInt(key)] = updatedShowCustom[key];
        reindexedCustomValue[parseInt(key)] = updatedCustomValue[key];
      }
    });
    
    setShowCustomInput(reindexedShowCustom);
    setCustomCategoryValue(reindexedCustomValue);
  };

  const handleAddCustomCategory = (index) => {
    setShowCustomInput(prev => ({
      ...prev,
      [index]: true
    }));
  };

  const handleCustomCategoryChange = (index, value) => {
    setCustomCategoryValue(prev => ({
      ...prev,
      [index]: value
    }));
  };

  const handleSaveCustomCategory = (index) => {
    const customValue = customCategoryValue[index]?.trim();
    
    if (!customValue) {
      toast.error("Please enter a category name");
      return;
    }

    // Check if category already exists in custom list
    if (customPrizeCategories.includes(customValue)) {
      toast.error("This category already exists");
      return;
    }

    // Add to custom categories
    setCustomPrizeCategories(prev => [...prev, customValue]);
    
    // Set the prize category to the new custom value
    handlePrizeChange(index, "prizeCategory", customValue);
    
    // Hide input
    setShowCustomInput(prev => ({
      ...prev,
      [index]: false
    }));
    
    // Clear input value
    setCustomCategoryValue(prev => ({
      ...prev,
      [index]: ""
    }));

    toast.success("Custom category added!");
  };

  const handleCancelCustomCategory = (index) => {
    setShowCustomInput(prev => ({
      ...prev,
      [index]: false
    }));
    setCustomCategoryValue(prev => ({
      ...prev,
      [index]: ""
    }));
  };

  const validateForm = () => {
    if (!formData.title) {
      toast.error("Title is required");
      return false;
    }

    if (!formData.campCateId) {
      toast.error("Please select a campaign category");
      return false;
    }

    const selectedCat = getSelectedCategory();
    if (!selectedCat) {
      toast.error("Invalid category selected");
      return false;
    }

    const isDaily = selectedCat.name?.toLowerCase() === "daily";

    // Campaign images are required for ALL campaign types
    if (campaignImages.length === 0) {
      toast.error("Campaign images are required");
      return false;
    }

    // Validate prizes
    for (let i = 0; i < prizes.length; i++) {
      // Prize images are required for ALL campaign types
      if (!prizeImages[i]) {
        toast.error(`Prize image is required for prize ${i + 1}`);
        return false;
      }

      // For daily campaigns, validate prize amount
      if (isDaily) {
        if (!prizes[i].prizeAmount || prizes[i].prizeAmount <= 0) {
          toast.error(`Valid prize amount is required for prize ${i + 1}`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      const selectedCat = getSelectedCategory();
      const isDaily = selectedCat.name?.toLowerCase() === "daily";
      
      const formDataToSend = new FormData();
      
      // Append basic fields
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Append campaign images (field name: "images") - for ALL campaigns
      campaignImages.forEach(image => {
        formDataToSend.append("images", image);
      });

      // Prepare prizes array
      const prizesData = prizes.map((prize, index) => {
        const basePrize = {
          prizeName: prize.prizeName || (isDaily ? "Cash Prize" : `Prize ${index + 1}`),
          prizeCategory: prize.prizeCategory || (isDaily ? "cash" : "general"),
          description: prize.description || "",
          position: index + 1
        };

        if (isDaily) {
          return {
            ...basePrize,
            prizeAmount: prize.prizeAmount || 0
          };
        } else {
          return basePrize;
        }
      });

      // Append prizes as JSON string
      formDataToSend.append("prizes", JSON.stringify(prizesData));

      // Append prize images (field name: "prizeImages") - for ALL campaigns
      Object.values(prizeImages).forEach(image => {
        formDataToSend.append("prizeImages", image);
      });

      // Log FormData contents for debugging
      console.log("Submitting FormData:");
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0], pair[1]);
      }

      const response = await axios.post(
        `${apiUrl}/api/admin/createcampaign`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      if (response.data.success) {
        toast.success("Campaign created successfully!");
        
        // Clean up previews
        imagePreviews.forEach(preview => URL.revokeObjectURL(preview));
        Object.values(prizeImagePreviews).forEach(preview => URL.revokeObjectURL(preview));
        
        // Navigate to campaigns page
        navigate("/camp");
      }
    } catch (error) {
      console.error("Error creating campaign:", error);
      toast.error(error.response?.data?.message || "Failed to create campaign");
    } finally {
      setLoading(false);
    }
  };

  const selectedCategory = getSelectedCategory();
  const isDaily = isDailyCampaign();
  const isCategorySel = isCategorySelected();
  const prizeCategoryOptions = selectedCategory ? getPrizeCategoryOptions(isDaily) : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiInfo className="text-blue-500" />
            Create New Campaign
          </h1>
          <p className="text-gray-600 mt-2 text-sm md:text-base">
            Fill in the details below to create a new campaign
          </p>
          {selectedCategory && (
            <div className="mt-2 space-y-1">
              <p className="text-sm font-medium">
                Category: <span className="text-blue-600">{selectedCategory.name}</span>
              </p>
              <p className="text-xs text-gray-500">
                {isDaily ? '💰 Cash Prize Campaign' : '🎁 Product Prize Campaign'}
              </p>
            </div>
          )}
        </div>

        {/* Main Form */}
        <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-8">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 md:mb-6 flex items-center gap-2">
              <FiInfo className="text-blue-500" />
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  placeholder="Enter campaign title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiFolder className="text-purple-500" />
                    Campaign Category *
                  </div>
                </label>
                {loadingCategories ? (
                  <div className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg bg-gray-50">
                    <FiLoader className="animate-spin text-blue-500" />
                    <span className="text-sm text-gray-600">Loading categories...</span>
                  </div>
                ) : (
                  <select
                    name="campCateId"
                    value={formData.campCateId}
                    onChange={handleInputChange}
                    className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category._id} value={category._id}>
                        {category.name.charAt(0).toUpperCase() + category.name.slice(1)} 
                        {category.campaignCount > 0 ? ` (${category.campaignCount} campaigns)` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiDollarSign className="text-green-500" />
                    Price (₹) *
                  </div>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  placeholder="Enter price"
                  min="0"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiFileText className="text-gray-500" />
                    Description
                  </div>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  placeholder="Enter campaign description"
                />
              </div>
            </div>
          </div>

          {/* Dates Section */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-8">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 md:mb-6 flex items-center gap-2">
              <FiCalendar className="text-green-500" />
              Campaign Dates
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiClock className="text-blue-500" />
                    Start Date *
                  </div>
                </label>
                <input
                  type="datetime-local"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiClock className="text-red-500" />
                    End Date *
                  </div>
                </label>
                <input
                  type="datetime-local"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiClock className="text-purple-500" />
                    Registration End Date *
                  </div>
                </label>
                <input
                  type="datetime-local"
                  name="registrationEndDate"
                  value={formData.registrationEndDate}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  required
                />
              </div>
            </div>
          </div>

          {/* Tickets & Winners */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-8">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 md:mb-6 flex items-center gap-2">
              <RiTicketLine className="text-purple-500" />
              Tickets & Winners
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <RiTicketLine className="text-blue-500" />
                    Number of Tickets *
                  </div>
                </label>
                <input
                  type="number"
                  name="noOfTickets"
                  value={formData.noOfTickets}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  placeholder="Enter total tickets"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiUsers className="text-green-500" />
                    Number of Winners *
                  </div>
                </label>
                <input
                  type="number"
                  name="noOfWinners"
                  value={formData.noOfWinners}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  placeholder="Enter number of winners"
                  min="1"
                  required
                />
              </div>
            </div>
          </div>

          {/* Campaign Images - For ALL campaign types */}
          {isCategorySel && (
            <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-8">
              <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 md:mb-6 flex items-center gap-2">
                <FiImage className="text-orange-500" />
                Campaign Images (Max 5) *
              </h2>
              
              <div className="space-y-4">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleCampaignImages}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  required
                />
                
                {imagePreviews.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4 mt-4">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-20 md:h-24 object-cover rounded-lg border-2 border-gray-200"
                        />
                      </div>
                    ))}
                  </div>
                )}
                {imagePreviews.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Campaign images are required</p>
                )}
              </div>
            </div>
          )}

          {/* Prizes Section */}
          {isCategorySel && (
            <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-8">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 md:mb-6">
                <h2 className="text-lg md:text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <FiGift className="text-pink-500" />
                  Prizes (Max 10) 
                  {isDaily ? ' (Cash Prizes)' : ' (Product Prizes)'}
                </h2>
                <button
                  type="button"
                  onClick={addPrize}
                  className="w-full sm:w-auto px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center justify-center gap-2 text-sm md:text-base"
                >
                  <FiPlus />
                  Add Prize
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                {prizes.map((prize, index) => (
                  <div key={index} className="p-3 md:p-4 border border-gray-200 rounded-xl relative">
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => removePrize(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 md:w-8 md:h-8 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all flex items-center justify-center z-10"
                      >
                        <FiX className="text-sm md:text-base" />
                      </button>
                    )}
                    
                    <div className="grid grid-cols-1 gap-3 md:gap-4">
                      {/* Prize Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Prize {index + 1} Name {isDaily ? '(Optional)' : '*'}
                        </label>
                        <input
                          type="text"
                          value={prize.prizeName}
                          onChange={(e) => handlePrizeChange(index, "prizeName", e.target.value)}
                          className="w-full px-3 md:px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                          placeholder={isDaily ? "Cash Prize (default)" : "Enter prize name"}
                        />
                      </div>

                      {/* Prize Category with Custom Option */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Prize Category
                        </label>
                        {showCustomInput[index] ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={customCategoryValue[index] || ""}
                              onChange={(e) => handleCustomCategoryChange(index, e.target.value)}
                              className="flex-1 px-3 md:px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                              placeholder="Enter custom category"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveCustomCategory(index)}
                              className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all text-sm"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCancelCustomCategory(index)}
                              className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <select
                              value={prize.prizeCategory}
                              onChange={(e) => handlePrizeChange(index, "prizeCategory", e.target.value)}
                              className="flex-1 px-3 md:px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                            >
                              {prizeCategoryOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => handleAddCustomCategory(index)}
                              className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all text-sm whitespace-nowrap"
                            >
                              + Custom
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Prize Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Prize Description (Optional)
                        </label>
                        <textarea
                          value={prize.description}
                          onChange={(e) => handlePrizeChange(index, "description", e.target.value)}
                          rows="2"
                          className="w-full px-3 md:px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                          placeholder="Enter prize description"
                        />
                      </div>

                      {/* Prize Amount for Daily Campaigns */}
                      {isDaily && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Prize Amount (₹) *
                          </label>
                          <input
                            type="number"
                            value={prize.prizeAmount}
                            onChange={(e) => handlePrizeChange(index, "prizeAmount", e.target.value)}
                            className="w-full px-3 md:px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                            placeholder="Enter prize amount"
                            min="0"
                            required={isDaily}
                          />
                        </div>
                      )}

                      {/* Prize Image - For ALL campaign types */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Prize Image *
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handlePrizeImage(index, e)}
                          className="w-full px-3 md:px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          required={!prizeImages[index]}
                        />
                        {!prizeImages[index] && (
                          <p className="text-xs text-red-500 mt-1">Prize image is required</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Prize Image Preview */}
                    {prizeImagePreviews[index] && (
                      <div className="mt-3 md:mt-4">
                        <img
                          src={prizeImagePreviews[index]}
                          alt={`Prize ${index + 1}`}
                          className="w-20 h-20 md:w-32 md:h-32 object-cover rounded-lg border-2 border-gray-200"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => navigate("/camp")}
              className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm md:text-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || loadingCategories || !isCategorySel}
              className="w-full sm:w-auto px-6 md:px-8 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm md:text-base"
            >
              {loading ? (
                <>
                  <FiLoader className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FiCheck />
                  Create Campaign
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCampaign;